import React, { useCallback, useMemo, useRef } from 'react';

import { FormikTextInput } from 'src/common/form/formik/text-input';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { Nullable } from 'src/types/nullable.type';
import { generateJourneyAliasShareUrl } from 'src/utils/journey';

import { JourneyAliasFormType } from './link-config-form.factory';
import { useCurrentOrganization } from 'src/store/organization';
import { useFormikContext } from 'formik';
import SharePanelFormEmbedSectionLink from './sections/embed.section';
import SharePanelFormAccessSectionLink from './sections/access.section';
import SharePanelFormEmailParamsSectionLink from './sections/email-params.section';
import SharePanelFormUTMParamsSectionLink from './sections/utm-params.section';
import ShareHubSpotFormPanel from './sections/hubspot.section';
import { ButtonWithMenu } from 'src/common/button-with-menu';
import { functionNoop } from 'src/utils/function/noop';
import { ShareCopyLinkMenuOptions } from '../copy-link-menu-options';
import { useSharePanelStore } from 'src/common/store/share.store';
import { useShareThumbnailCodeStore } from '../thumbnail-code/store';
import { SharePanelFormConversationSectionLink } from './sections/conversation.section';
import SharePanelFormPersonalizationSectionLink from './sections/personalization.section';
import { SharePanelFormSection } from './sections/section';
import { SharePanelFormPresentationSection } from './sections/presentation.section';

export const ShareLinkConfigForm = ({ isNeueActive }: { isNeueActive: boolean }) => {
  const { setSuccessNotification } = useNotificationMessage();
  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);
  const copyLinkMenuOpenRef = useRef(functionNoop);
  const copyLinkMenuCloseRef = useRef(functionNoop);
  const sharedJourney = useSharePanelStore((state) => state.sharedJourney)!;
  const { generateThumbnailCode, resetThumbnailElement } = useShareThumbnailCodeStore(
    ({ generateThumbnailCode, resetThumbnailElement }) => ({
      generateThumbnailCode,
      resetThumbnailElement,
    })
  );

  const { values } = useFormikContext<JourneyAliasFormType>();

  const slugInlineAddonRef = useRef<Nullable<HTMLDivElement>>(null);
  const slugInputElementRef = useCallback((element) => {
    setTimeout(() => {
      if (element && slugInlineAddonRef.current) {
        element.setAttribute('style', `padding-left: ${slugInlineAddonRef.current.clientWidth}px`);
      }
    }, 1);
  }, []);

  const inlineAddonHref = useMemo(
    () => (
      <div
        ref={slugInlineAddonRef}
        className="absolute inset-y-1 border-transparent pointer-events-none flex items-center px-4 py-3 after:content-[''] after:absolute after:-inset-y-0.5 after:right-[7px] after:w-px after:bg-bedrock-gray-medium rounded-l-md text-bedrock-p cursor-default"
      >
        {`${currentOrganization.slug}.${process.env.ROOT_NETLOC?.split(':')[0]}/p/`}
      </div>
    ),
    [currentOrganization.slug]
  );

  const embedUrl = useMemo(
    () => generateJourneyAliasShareUrl(currentOrganization, values.slug),
    [currentOrganization, values]
  );

  const onCopyLinkClicked = () => {
    navigator.clipboard.writeText(embedUrl);
    setSuccessNotification('URL copied to clipboard');
  };

  const onCopyLinkMenuClicked = async () => {
    resetThumbnailElement();
    copyLinkMenuOpenRef.current();
    await generateThumbnailCode(sharedJourney.uuid, embedUrl);
  };

  const renderThumbnailLinkMenu = (menuButton: JSX.Element) => {
    return (
      <ShareCopyLinkMenuOptions openRef={copyLinkMenuOpenRef} closeRef={copyLinkMenuCloseRef} menuButton={menuButton} />
    );
  };

  return (
    <>
      <div className='flex flex-col divide-y relative'>
        <div className='flex flex-col space-y-sm py-6'>
          <div className='text-bedrock-p-strong text-bedrock-black'>Customize URL</div>
          <FormikTextInput
            id='journey-share-alias-form-slug'
            ref={slugInputElementRef}
            required
            inlineAddon={inlineAddonHref}
            hideOptional
            inputClasses='text-bedrock-p transition-none'
            name='slug'
            type='text'
          />
        </div>
        <SharePanelFormEmailParamsSectionLink />
        <SharePanelFormUTMParamsSectionLink />
        <SharePanelFormAccessSectionLink />
        <SharePanelFormSection type='indexing' title='Google Index' subTitle='Control Google indexing for this link' />
        <SharePanelFormPresentationSection />
        <SharePanelFormConversationSectionLink />
        {!isNeueActive && (
          <>
            <SharePanelFormPersonalizationSectionLink />
          </>
        )}
        <SharePanelFormEmbedSectionLink />
        <ShareHubSpotFormPanel />
      </div>
      <div className='sticky bottom-0 left-0 right-0 w-full flex flex-row space-x-md pt-4 bg-white'>
        <ButtonWithMenu
          type='button'
          variant='primary'
          buttonClassName='w-full'
          containerClassName='w-full'
          onButtonClick={onCopyLinkClicked}
          onMenuClick={onCopyLinkMenuClicked}
          label='Copy link'
          renderMenu={renderThumbnailLinkMenu}
        />
      </div>
    </>
  );
};
